import * as Sentry from '@sentry/react';
import { ComponentProps, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { AccountView } from '../../../types/entity-types';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { PaymentFlowStepWrapper } from '../components/payment-flow-step-wrapper';
import { usePaymentFlow } from '../use-payment-flow';
import { InternalPaymentForm } from './components/internal-payment-form';
import { SuccessStep } from './components/success-step';

export type InternalPaymentFlowStep = 'PAYMENT_FORM' | 'SUCCESS';

type InternalPaymentFlowProps = {
  accounts: AccountView[];
  isProcessingPayment: boolean;
  paymentStep: InternalPaymentFlowStep;
  onSubmit: ComponentProps<typeof InternalPaymentForm>['onSubmit'];
  onGoBack: () => void;
  onSuccessfulAuthentication: () => void;
};

export const InternalPaymentFlow = ({
  accounts,
  isProcessingPayment,
  onGoBack,
  onSubmit,
  paymentStep,
  onSuccessfulAuthentication,
}: InternalPaymentFlowProps) => {
  const { authorisationStatus, clearPaymentDetails } = usePaymentFlow();

  // Cached because it is a dependency of useEffect
  const cachedOnSuccessfulAuthentication = useCallback(() => {
    onSuccessfulAuthentication();
  }, [onSuccessfulAuthentication]);

  const cachedClearPaymentDetails = useCallback(() => {
    clearPaymentDetails();
  }, [clearPaymentDetails]);

  useEffect(() => {
    if (paymentStep === 'PAYMENT_FORM' && authorisationStatus === 'success') {
      cachedOnSuccessfulAuthentication();

      return;
    }

    if (paymentStep === 'PAYMENT_FORM' && authorisationStatus === 'error') {
      toast.error('Authentication failed. Please try again.');
      cachedClearPaymentDetails();

      return;
    }
  }, [authorisationStatus, cachedClearPaymentDetails, cachedOnSuccessfulAuthentication, paymentStep]);

  return (
    <InternalPaymentFlowStep
      step={paymentStep}
      accounts={accounts}
      isProcessingPayment={isProcessingPayment}
      onSubmit={onSubmit}
      onGoBack={onGoBack}
    />
  );
};

type InternalPaymentFlowStepProps = {
  step: InternalPaymentFlowStep;
  accounts: AccountView[];
  isProcessingPayment: boolean;
  onSubmit: ComponentProps<typeof InternalPaymentForm>['onSubmit'];
  onGoBack: () => void;
};

const InternalPaymentFlowStep = ({
  step,
  accounts,
  onSubmit,
  isProcessingPayment,
  onGoBack,
}: InternalPaymentFlowStepProps) => {
  const { getPaymentDetails } = usePaymentFlow();

  switch (step) {
    case 'PAYMENT_FORM': {
      const paymentDetails = getPaymentDetails();

      return (
        <PaymentFlowStepWrapper title="Make Transfer" onGoBack={onGoBack}>
          <InternalPaymentForm
            accounts={accounts}
            initialValues={{
              sourceAccountId: paymentDetails?.sourceAccountId,
              destinationAccountId: paymentDetails?.destinationAccountId,
            }}
            onSubmit={onSubmit}
            isLoading={isProcessingPayment}
          />
        </PaymentFlowStepWrapper>
      );
    }
    case 'SUCCESS': {
      const paymentDetails = getPaymentDetails();

      if (
        !paymentDetails?.amount ||
        !paymentDetails?.sourceAccountName ||
        !paymentDetails?.sourceAccountLogo ||
        !paymentDetails?.destinationAccountName ||
        !paymentDetails?.destinationAccountLogo ||
        !paymentDetails?.currency
      ) {
        Sentry.captureMessage('Payment details are not set in SUCCESS step in InternalPaymentFlow', {
          extra: {
            paymentDetails,
            step,
            message: 'Payment details are not set in SUCCESS step in InternalPaymentFlow',
          },
        });

        return null;
      }

      const {
        amount,
        sourceAccountName,
        destinationAccountName,
        currency,
        sourceAccountLogo,
        destinationAccountLogo,
        isInstantTransfer,
      } = paymentDetails;

      return (
        <SuccessStep
          from={{ name: sourceAccountName, imageUrl: sourceAccountLogo }}
          to={{ name: destinationAccountName, imageUrl: destinationAccountLogo }}
          amount={getAmountWithCurrency({ amount, currency, isAccountBalance: true })}
          isInstantTransfer={isInstantTransfer}
          onDone={onGoBack}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
